import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
    EventEmitter,
    OnDestroy,
} from '@angular/core';
import {
    ITask2ReportParameter,
    ITask2ReportSummary,
    ITask2ReportWidget,
    ITaskPeriodDateRange,
} from '../fi-task-v2-report';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiTaskV2ReportHelperService } from '../services/fi-task-v2-report-helper.service';
import { IReportWidget } from '@fi-shared/fi-report-widget/fi-report-widget';
import { IItemsListAction2 } from '@fi-shared/fi-items-list/fi-items-list';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { FiTaskReportDateType } from '../fi-task-v2-report-enums';
import { FiTask2ReportDateService } from '../services/fi-task-v2-report-date.service';
import { IPeriodDateRange } from '@fi-shared/fi-time-range-menu/fi-time-range-menu';
import { AppTextService } from '@fi-shared/app-text';
import { FiItemsListOptionType } from '@fi-shared/fi-items-list/fi-items-list-option-type';
import { FiReportWidgetDialogsService } from '@fi-shared/fi-report-widget/fi-report-widget-dialogs.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { FiTaskV2ReportService } from '@fi-app/task-v2/fi-task-v2-report/services/fi-task-v2-report.service';

@Component({
    selector: 'fi-task-v2-report-widget',
    templateUrl: './fi-task-v2-report-widget.component.html',
    styleUrl: './fi-task-v2-report-widget.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-task-v2-report-widget' },
    standalone: false,
})
export class FiTaskV2ReportWidgetComponent implements OnInit, OnDestroy {
    @Input({ required: true }) widget: ITask2ReportWidget;
    @Input({ required: true }) initialSummary: ITask2ReportSummary;
    @Output() deleteWidget = new EventEmitter<ITask2ReportWidget>();
    @Output() editWidget = new EventEmitter<ITask2ReportWidget>();

    private _widgetData$ = new BehaviorSubject<{
        widget: IReportWidget;
        summary: ITask2ReportSummary;
    }>(undefined);
    protected get widgetData$() {
        return this._widgetData$.asObservable();
    }

    protected actions: IItemsListAction2<ITask2ReportWidget>[];
    protected timeIntervalOptions: INameIdObj[] = this.fiTaskV2ReportHelperService.taskReportDateOptions;
    protected readonly FiTaskReportDateType = FiTaskReportDateType;
    protected taskDateRange: ITaskPeriodDateRange;

    constructor(
        private fiStateService: FiStateService,
        private fiTaskV2ReportService: FiTaskV2ReportService,
        private fiTaskV2ReportHelperService: FiTaskV2ReportHelperService,
        private fiTask2ReportDateService: FiTask2ReportDateService,
        private appTextService: AppTextService,
        private fiReportWidgetDialogsService: FiReportWidgetDialogsService,
    ) {}

    public ngOnInit(): void {
        this.setWidgetData(this.initialSummary);
        this.fiTask2ReportDateService.resetDateRange(this.widget.Id);
        this.taskDateRange = this.fiTask2ReportDateService.getDateRange(this.widget.Id);
        this.setActions();
        this.subscribeOnDateRangeChanged();
    }

    private updateSummary(): Promise<void> {
        const currentFilter = this.fiTaskV2ReportHelperService.getReportWidgetFilter(this.widget);
        const parameter: ITask2ReportParameter = {
            ReportType: this.widget.ReportType,
            Filter: currentFilter,
            DateRange: this.fiTask2ReportDateService.getDateRange(this.widget.Id),
            LoadSummary: true,
            LoadAvailableItems: false,
        };

        this.clearWidgetData();
        return this.fiTaskV2ReportService.getReportSummary(parameter).then((summary) => {
            this.setWidgetData(summary);
        });
    }

    private subscribeOnDateRangeChanged(): void {
        this.fiTask2ReportDateService.subscribeOnDateRangeChanged(this.widget.Id, this.ngOnDestroy$, () => {
            this.updateSummary();
        });
    }

    private setWidgetData(summary: ITask2ReportSummary) {
        this._widgetData$.next({
            widget: {
                icon: this.getWidgetIconClass(),
                description: this.getWidgetDescription(),
                summaryCount: this.getSummaryCount(summary),
            },
            summary: summary,
        });
    }

    private clearWidgetData() {
        this._widgetData$.next(undefined);
    }

    protected setTaskDateRange(newRange: ITaskPeriodDateRange): void {
        this.fiTask2ReportDateService.setNewDateRange(this.widget.Id, newRange);
    }

    protected isCustomWidget(): boolean {
        return this.fiTaskV2ReportHelperService.isCustomReportWidget(this.widget);
    }

    protected getTaskReportName(): string {
        return this.fiTaskV2ReportHelperService.getTaskReportName(this.widget);
    }

    protected openWidget(isFilterExpanded: boolean): void {
        this.fiStateService.go(FiStateNames.TaskV2.Report.Widget, {
            id: this.widget.Id,
            isFilterExpanded: isFilterExpanded,
        });
    }

    protected isOverdueWidget(): boolean {
        return this.fiTaskV2ReportHelperService.isOverdueWidget(this.widget);
    }

    private setActions(): void {
        this.actions = [
            {
                title: this.appTextService.get('widget.rename_widget'),
                condition: () => this.isCustomWidget(),
                iconClass: 'far fa-pen-line',
                optionType: FiItemsListOptionType.Icon,
                onClick: () => this.openRenameWidgetDialog(),
            },
            {
                title: this.appTextService.get('checklist.delete_report_widget'),
                condition: () => this.isCustomWidget(),
                iconClass: 'far fa-trash',
                optionType: FiItemsListOptionType.Icon,
                onClick: () => this.openDeleteWidgetDialog(),
            },
        ];
    }

    private openRenameWidgetDialog(): void {
        this.fiReportWidgetDialogsService
            .getRenameWidgetDialog(this.widget.Name, this.widget.Description)
            .onYesButtonClick((result) => {
                this.widget.Name = result.widgetName;
                this.widget.Description = result.widgetDescription;
                this.editWidget.emit(this.widget);
            });
    }
    private openDeleteWidgetDialog(): void {
        this.fiReportWidgetDialogsService.getConfirmWidgetDeleteDialog().onYesButtonClick(() => {
            this.deleteWidget.emit(this.widget);
        });
    }

    private getSummaryCount(summary: ITask2ReportSummary): number {
        return summary.CompletedCount + summary.InProgressCount + summary.NotStartedCount + summary.OverdueCount;
    }

    private getWidgetDescription(): string {
        return this.fiTaskV2ReportHelperService.getTaskReportDescription(this.widget);
    }

    private getWidgetIconClass(): string {
        return this.fiTaskV2ReportHelperService.getTaskReportWidgetIconClass(this.widget);
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
